import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { PmModalComponent } from '../pm-modal/pm-modal.component';
import { toDisplayCase } from '../../../utils/pm-display-case';

/** One row from a Companies House search. Deliberately its own shape rather than the API's type:
 *  a shared popup should not carry an import from the client-directory service layer. */
export interface PmCompaniesHouseResult {
  companyNumber: string;
  companyName: string;
  /** 'active', 'dissolved', 'liquidation' … — drives the badge tone. */
  companyStatus?: string;
  /** Incorporation date, already formatted by the caller. */
  dateOfCreation?: string;
  addressSnippet?: string;
}

/**
 * Add Client from Companies House — search, then pick a company to pre-fill the client form.
 *
 * Owns none of the searching. The caller runs the request and feeds `results`, `searching`, `error`
 * and `applyingId` back in; this renders them and reports intent. That split matters more here than
 * elsewhere: Companies House is rate-limited per key, so the page needs to decide when a request is
 * worth making, not have a component fire one on every keystroke.
 */
@Component({
  selector: 'pm-companies-house-modal',
  standalone: true,
  imports: [PmModalComponent],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './pm-companies-house-modal.component.html',
  styleUrls: ['./pm-companies-house-modal.component.css', '../pm-form-field.css']
})
export class PmCompaniesHouseModalComponent {
  @Input() query = '';
  @Input() searching = false;
  @Input() error: string | null = null;
  @Input() results: readonly PmCompaniesHouseResult[] = [];
  /** The company number currently being applied, so only that row shows its spinner. */
  @Input() applyingId: string | null = null;
  /** True once a search has run, so "no matches" is only shown after one. */
  @Input() searched = false;

  @Output() queryChange = new EventEmitter<string>();
  @Output() search = new EventEmitter<string>();
  @Output() use = new EventEmitter<PmCompaniesHouseResult>();
  @Output() closed = new EventEmitter<void>();

  get canSearch(): boolean {
    return !this.searching && this.query.trim().length > 1;
  }

  onQueryInput(event: Event): void {
    this.queryChange.emit((event.target as HTMLInputElement).value);
  }

  /** Enter searches, so the button is a target rather than the only way. */
  onQueryKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.runSearch();
  }

  runSearch(): void {
    if (!this.canSearch) return;
    this.search.emit(this.query.trim());
  }

  isApplying(item: PmCompaniesHouseResult): boolean {
    return this.applyingId === item.companyNumber;
  }

  /** Any row is locked while one is being applied — two companies cannot fill one form. */
  get anyApplying(): boolean {
    return this.applyingId !== null;
  }

  /** The register stores names in capitals; a column of shouting is hard to scan. */
  displayName(name: string): string {
    return toDisplayCase(name);
  }

  statusTone(status: string | undefined): string {
    const s = (status ?? '').toLowerCase();
    if (s === 'active') return 'green';
    if (s.includes('liquidation') || s.includes('administration')) return 'amber';
    if (s === 'dissolved' || s.includes('closed')) return 'red';
    return 'slate';
  }
}
